import { useEffect, useMemo, useRef } from 'react';
import type { CalculatePriceMultiRoutesAPI } from '@hero/api';
import { resolveLoadPackingAndQuantityFromVehicles } from '@hero/core';
import { useCalculatePriceApi } from '@hero/api-hooks';
import {
  buildDraftPriceCalcRequest,
  useCurrentUser,
  usePublishOrderStore,
} from '@hero/hooks-shared';

/**
 * Manager hook that drives the multi-route price calculation for the
 * `PublishOrderPage`. Reads the current draft (origin, destinations,
 * vehicle, customer) from `usePublishOrderStore` and writes the
 * resolver's response back into `state.pricePreview`. Per-destination
 * UI (`RouteSection`'s price chip) and the side-panel total breakdown
 * read from there — the manager itself is fire-and-forget.
 *
 * When no vehicle is picked yet, the resolver is still called (vehicle
 * omitted) so directed / everyone Routes on the lane can surface a
 * price and {@link routeVehicles} can seed the load draft.
 *
 * Debounced lightly (200 ms) so that the manager doesn't fire every
 * keystroke while the user is still picking a destination on the map.
 */
export function usePublishOrderPricingManager(): {
  isPending: boolean;
  isError: boolean;
} {
  const { orgId } = useCurrentUser();

  const customerId = usePublishOrderStore(s => s.customer?.customerId);
  const isInternalShipment = usePublishOrderStore(s => s.isInternalShipment);
  const origin = usePublishOrderStore(s => s.origin);
  const destinations = usePublishOrderStore(s => s.destinations);
  const vehicleType = usePublishOrderStore(s => s.load.vehicleType);
  const vehicleTypes = usePublishOrderStore(s => s.load.vehicleTypes);
  const loadOverrides = usePublishOrderStore(s => s.loadOverrides);
  const setPricePreview = usePublishOrderStore(s => s.setPricePreview);
  const applyLoadDefaults = usePublishOrderStore(s => s.applyLoadDefaults);

  const calc = useCalculatePriceApi();

  const request = useMemo<CalculatePriceMultiRoutesAPI['req'] | null>(() => {
    if (!orgId) return null;
    return buildDraftPriceCalcRequest({
      orgId,
      customerId: customerId ?? undefined,
      isInternalShipment,
      origin: origin ? { lat: origin.lat, lng: origin.lng } : null,
      destinations: destinations
        .filter(d => d && d.lat !== 0)
        .map(d => ({ lat: d.lat, lng: d.lng })),
      vehicleType: vehicleType ?? undefined,
      vehicleTypes,
      loadOverrides,
      requirePartyContext: !isInternalShipment,
    });
  }, [orgId, customerId, isInternalShipment, origin, destinations, vehicleType, vehicleTypes, loadOverrides]);

  const signature = useMemo(() => {
    if (!request) return null;
    return JSON.stringify(request);
  }, [request]);

  const lastSignatureRef = useRef<string | null>(null);

  useEffect(() => {
    if (!signature || !request) {
      if (lastSignatureRef.current !== null) {
        setPricePreview(null);
        lastSignatureRef.current = null;
      }
      return;
    }
    if (lastSignatureRef.current === signature) return;

    let cancelled = false;
    const timer = setTimeout(() => {
      calc
        .mutateAsync(request)
        .then(data => {
          if (cancelled) return;
          lastSignatureRef.current = signature;
          setPricePreview(data ?? null);

          // Seed vehicles from the matched Route when the user hasn't
          // picked a type yet — directed rate first, then everyone rate.
          const firstLeg = data?.routes?.[0];
          const routeVehicles = firstLeg?.routeVehicles;
          const userPickedVehicle =
            loadOverrides.vehicleType || loadOverrides.vehicleTypes;
          if (
            routeVehicles?.length &&
            !vehicleType &&
            !userPickedVehicle
          ) {
            const derived = resolveLoadPackingAndQuantityFromVehicles(routeVehicles);
            applyLoadDefaults({
              defaults: {
                vehicleTypes: routeVehicles,
                vehicleType: routeVehicles[0],
                ...(!loadOverrides.packing ? { packing: derived.packing } : {}),
                ...(!loadOverrides.quantity ? { quantity: derived.quantity } : {}),
              },
            });
          }
        })
        .catch(() => {
          if (cancelled) return;
          setPricePreview(null);
        });
    }, 200);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature]);

  return {
    isPending: calc.isPending,
    isError: calc.isError,
  };
}
